import { Settings } from 'lucide-react';

import { actionColor, actionLabel, type AuditRow } from '../auditViewModel';
import { avatarColorFor } from '../roleTaxonomy';

import UserAvatar from './UserAvatar';

interface AuditTableProps {
  rows: AuditRow[];
}

const TH =
  'text-left py-2.5 text-[10px] font-semibold tracking-[.08em] uppercase text-stone-500 border-b border-stone-200';
const TD = 'py-2.5 px-3 border-b border-stone-200 align-middle';

function RoleDiff({ row }: { row: AuditRow }) {
  return (
    <span className="font-mono text-[11.5px]">
      <span className="text-stone-500 dark:text-stone-400 line-through">{String(row.before)}</span>
      <span className="mx-1.5 text-stone-500 dark:text-stone-400">→</span>
      <span className="text-moss-700 font-bold">{String(row.after)}</span>
    </span>
  );
}

function RegionDiff({ row }: { row: AuditRow }) {
  const before = Array.isArray(row.before) ? row.before.length : 0;
  const after = Array.isArray(row.after) ? row.after.length : 0;
  return (
    <span className="font-mono text-[11.5px] text-stone-600 dark:text-stone-400">
      regions: <strong>{before}</strong> →{' '}
      <strong className={row.action === 'region-added' ? 'text-moss-700' : 'text-clay-600'}>
        {after}
      </strong>
    </span>
  );
}

function ActiveDiff({ row }: { row: AuditRow }) {
  return (
    <span className="font-mono text-[11.5px] text-stone-600 dark:text-stone-400">
      isActive: <strong>{row.action === 'reactivated' ? 'true' : 'false'}</strong>
    </span>
  );
}

function FieldDiff({ row }: { row: AuditRow }) {
  if (!row.field)
    return <span className="text-[11.5px] text-stone-400 dark:text-stone-500">—</span>;
  return (
    <span className="font-mono text-[11.5px] text-stone-600 dark:text-stone-400">
      {row.field}: <strong>updated</strong>
    </span>
  );
}

function DiffCell({ row }: { row: AuditRow }) {
  if (row.action === 'role-changed') return <RoleDiff row={row} />;
  if (row.action === 'region-added' || row.action === 'region-removed')
    return <RegionDiff row={row} />;
  if (row.action === 'deactivated' || row.action === 'reactivated') return <ActiveDiff row={row} />;
  if (row.action === 'invited')
    return (
      <span className="font-mono text-[11.5px] text-stone-600 dark:text-stone-400">
        new user invited
      </span>
    );
  return <FieldDiff row={row} />;
}

function ActorCell({ actor }: { actor: string }) {
  if (actor === 'system' || actor === 'System') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono text-stone-500 dark:text-stone-400 bg-stone-50 dark:bg-[#1e2520] border border-stone-200 dark:border-stone-800">
        <Settings size={11} />
        system
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <UserAvatar name={actor} size={20} color={avatarColorFor(actor)} />
      <span className="text-[12px] text-stone-600 dark:text-stone-400">{actor}</span>
    </div>
  );
}

/** Chronological audit-event table. */
export default function AuditTable({ rows }: AuditTableProps) {
  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="w-full border-collapse text-[13px]">
        <thead className="sticky top-0 bg-white dark:bg-[#1a1f1c] z-[1]">
          <tr>
            {['When', 'Action', 'Target', 'Actor', 'Diff'].map((h, i) => (
              <th
                key={h}
                className={TH}
                style={{
                  paddingLeft: i === 0 ? 18 : 12,
                  paddingRight: i === 4 ? 18 : 12,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={5}
                className="py-12 text-center text-[12.5px] text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-800"
              >
                No audit events yet. Role, region, status and invite changes will show up here as
                staff data evolves.
              </td>
            </tr>
          )}
          {rows.map((e) => (
            <tr key={e.id} className="hover:bg-stone-50 dark:hover:bg-[#242d28]">
              <td className="py-2.5 pl-[18px] pr-3 border-b border-stone-200 dark:border-stone-800 whitespace-nowrap">
                <div className="text-[12px] font-semibold text-bark-800 dark:text-[#e8ece9]">
                  {e.ts.split(',')[0]}
                </div>
                <div className="text-[10.5px] text-stone-500 dark:text-stone-400 tabular-nums">
                  {e.ts.split(',').slice(1).join(',').trim()}
                </div>
              </td>
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <i
                    className="w-1.5 h-1.5 rounded-full flex-none"
                    style={{ background: actionColor(e.action) }}
                  />
                  <span className="text-[12px] font-semibold">{actionLabel(e.action)}</span>
                </div>
              </td>
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <UserAvatar name={e.target} size={22} />
                  <span className="text-[12.5px] font-medium">{e.target}</span>
                </div>
              </td>
              <td className={TD}>
                <ActorCell actor={e.actor} />
              </td>
              <td className="py-2.5 px-3 pr-[18px] border-b border-stone-200 dark:border-stone-800">
                <DiffCell row={e} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
